import {
  Activity,
  BarChart3,
  BookOpen,
  Clock,
  History,
  Settings,
  TrendingUp,
  Users,
} from "lucide-react";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../../components/ui/card";
import { useAuth } from "../../../contexts/AuthContext";
import {
  ActividadReciente,
  DashboardData,
  DashboardService,
} from "../../../services/admin/dashboard/dashboard.service";
import { AdminLayout } from "../components";

export const DashboardPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState<DashboardData>({
    stats: {
      mundosActivos: 0,
      etapasCreadas: 0,
      preguntasHistoria: 0,
      usuariosActivos: 0,
    },
    mundosStats: [],
  });
  const [actividadReciente, setActividadReciente] = useState<
    ActividadReciente[]
  >([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadDashboardData = async () => {
      try {
        setLoading(true);
        const [data, actividadData] = await Promise.all([
          DashboardService.getDashboardData(),
          DashboardService.getActividadReciente(),
        ]);

        setDashboardData(data);
        setActividadReciente(actividadData);
      } catch (error) {
        console.error("Error cargando datos del dashboard:", error);
      } finally {
        setLoading(false);
      }
    };

    loadDashboardData();
  }, []);

  const handleNavigation = (path: string) => {
    navigate(path);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = Math.floor(
      (now.getTime() - date.getTime()) / (1000 * 60 * 60)
    );

    if (diffInHours < 1) {
      return "Hace unos minutos";
    } else if (diffInHours < 24) {
      return `Hace ${diffInHours} horas`;
    } else {
      const diffInDays = Math.floor(diffInHours / 24);
      return `Hace ${diffInDays} días`;
    }
  };

  const getActivityIcon = (tipo: string) => {
    switch (tipo) {
      case "pregunta":
        return <div className="w-3 h-3 bg-green-500 rounded-full mt-2"></div>;
      case "etapa":
        return <div className="w-3 h-3 bg-blue-500 rounded-full mt-2"></div>;
      case "mundo":
        return <div className="w-3 h-3 bg-purple-500 rounded-full mt-2"></div>;
      case "sistema":
        return <div className="w-3 h-3 bg-orange-500 rounded-full mt-2"></div>;
      default:
        return <div className="w-3 h-3 bg-gray-500 rounded-full mt-2"></div>;
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="space-y-8 p-6">
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl shadow-xl p-8 text-white">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-4xl font-bold mb-3">
                  ¡Bienvenido a Fracto!
                </h1>
                <p className="text-xl text-blue-100">Cargando datos...</p>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[1, 2, 3, 4].map((i) => (
              <Card key={i} className="animate-pulse">
                <CardHeader className="pb-2">
                  <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                </CardHeader>
                <CardContent>
                  <div className="h-8 bg-gray-200 rounded w-1/2 mb-2"></div>
                  <div className="h-3 bg-gray-200 rounded w-1/3"></div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-8 p-6">
        {/* Header de bienvenida mejorado */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl shadow-xl p-8 text-white">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-bold mb-3">¡Bienvenido a Fracto!</h1>
              <p className="text-xl text-blue-100">
                Hola {user?.nombres} {user?.apellidos}, gestiona todo el
                contenido educativo
              </p>
            </div>
            <div className="hidden md:block">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center">
                <BookOpen className="w-8 h-8 text-white" />
              </div>
            </div>
          </div>
        </div>

        {/* Estadísticas mejoradas */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <Card className="hover:shadow-lg transition-all duration-300 border-0 shadow-md">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">
                Preguntas Historia
              </CardTitle>
              <History className="h-4 w-4 text-blue-600" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-gray-900">
                {dashboardData.stats.preguntasHistoria}
              </div>
              <p className="text-xs text-green-600 font-medium">
                Preguntas oficiales
              </p>
            </CardContent>
          </Card>

          <Card className="hover:shadow-lg transition-all duration-300 border-0 shadow-md">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">
                Mundos Activos
              </CardTitle>
              <BookOpen className="h-4 w-4 text-purple-600" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-gray-900">
                {dashboardData.stats.mundosActivos}
              </div>
              <p className="text-xs text-blue-600 font-medium">
                Mundos disponibles
              </p>
            </CardContent>
          </Card>

          <Card className="hover:shadow-lg transition-all duration-300 border-0 shadow-md">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">
                Etapas Creadas
              </CardTitle>
              <TrendingUp className="h-4 w-4 text-green-600" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-gray-900">
                {dashboardData.stats.etapasCreadas}
              </div>
              <p className="text-xs text-purple-600 font-medium">
                Etapas disponibles
              </p>
            </CardContent>
          </Card>

          <Card className="hover:shadow-lg transition-all duration-300 border-0 shadow-md">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">
                Usuarios Activos
              </CardTitle>
              <Users className="h-4 w-4 text-emerald-600" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-gray-900">
                {dashboardData.stats.usuariosActivos}
              </div>
              <p className="text-xs text-emerald-600 font-medium">
                Usuarios registrados
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Acciones rápidas mejoradas */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <Card className="border-0 shadow-md hover:shadow-lg transition-all duration-300">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Activity className="h-5 w-5 text-blue-600" />
                Acciones Rápidas
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <button
                  onClick={() => handleNavigation("/admin/modo-historia")}
                  className="p-6 border border-gray-200 rounded-xl hover:bg-gradient-to-br hover:from-blue-50 hover:to-purple-50 transition-all duration-300 group"
                >
                  <div className="flex flex-col items-center text-center space-y-2">
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center group-hover:bg-blue-200 transition-colors">
                      <BookOpen className="h-6 w-6 text-blue-600" />
                    </div>
                    <div className="font-semibold text-gray-900">
                      Gestionar Historia
                    </div>
                    <div className="text-sm text-gray-500">
                      Preguntas oficiales
                    </div>
                  </div>
                </button>

                <button
                  onClick={() => handleNavigation("/admin/gestion-curricular")}
                  className="p-6 border border-gray-200 rounded-xl hover:bg-gradient-to-br hover:from-purple-50 hover:to-pink-50 transition-all duration-300 group"
                >
                  <div className="flex flex-col items-center text-center space-y-2">
                    <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center group-hover:bg-purple-200 transition-colors">
                      <BarChart3 className="h-6 w-6 text-purple-600" />
                    </div>
                    <div className="font-semibold text-gray-900">
                      Malla Curricular
                    </div>
                    <div className="text-sm text-gray-500">
                      Estructura educativa
                    </div>
                  </div>
                </button>

                <button
                  onClick={() => handleNavigation("/admin/usuarios")}
                  className="p-6 border border-gray-200 rounded-xl hover:bg-gradient-to-br hover:from-green-50 hover:to-emerald-50 transition-all duration-300 group"
                >
                  <div className="flex flex-col items-center text-center space-y-2">
                    <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center group-hover:bg-green-200 transition-colors">
                      <Users className="h-6 w-6 text-green-600" />
                    </div>
                    <div className="font-semibold text-gray-900">
                      Gestionar Usuarios
                    </div>
                    <div className="text-sm text-gray-500">
                      Administrar Cuentas
                    </div>
                  </div>
                </button>

                <button
                  onClick={() => handleNavigation("/admin/configuracion")}
                  className="p-6 border border-gray-200 rounded-xl hover:bg-gradient-to-br hover:from-orange-50 hover:to-red-50 transition-all duration-300 group"
                >
                  <div className="flex flex-col items-center text-center space-y-2">
                    <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center group-hover:bg-orange-200 transition-colors">
                      <Settings className="h-6 w-6 text-orange-600" />
                    </div>
                    <div className="font-semibold text-gray-900">
                      Configuración
                    </div>
                    <div className="text-sm text-gray-500">
                      Ajustes del sistema
                    </div>
                  </div>
                </button>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-md hover:shadow-lg transition-all duration-300">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="h-5 w-5 text-purple-600" />
                Actividad Reciente
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                {actividadReciente.map((actividad) => (
                  <div
                    key={actividad.id}
                    className="flex items-start space-x-4"
                  >
                    {getActivityIcon(actividad.tipo)}
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-gray-900">
                        {actividad.descripcion}
                      </p>
                      <p className="text-xs text-gray-500">
                        {formatDate(actividad.fecha)}
                      </p>
                      {actividad.mundo && (
                        <p className="text-xs text-blue-600 font-medium">
                          {actividad.etapa
                            ? `${actividad.mundo} - ${actividad.etapa}`
                            : actividad.mundo}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Estadísticas adicionales */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card className="border-0 shadow-md bg-gradient-to-br from-blue-50 to-blue-100">
            <CardHeader>
              <CardTitle className="text-blue-800">
                Distribución por Mundos
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {dashboardData.mundosStats.map((mundo) => (
                  <div key={mundo.idMundoHistoria}>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-blue-700">
                        {mundo.nombre}
                      </span>
                      <span className="text-sm font-semibold text-blue-800">
                        {mundo.porcentaje}%
                      </span>
                    </div>
                    <div className="w-full bg-blue-200 rounded-full h-2">
                      <div
                        className="bg-blue-600 h-2 rounded-full"
                        style={{ width: `${mundo.porcentaje}%` }}
                      ></div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-md bg-gradient-to-br from-green-50 to-green-100">
            <CardHeader>
              <CardTitle className="text-green-800">
                Estado de Vigencia
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-center">
                <div className="text-4xl font-bold text-green-800 mb-2">
                  {dashboardData.stats.preguntasHistoria > 0 ? "100%" : "0%"}
                </div>
                <p className="text-sm text-green-700">Contenido vigente</p>
                <div className="mt-4 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-green-700">Vigente</span>
                    <span className="font-semibold text-green-800">
                      {dashboardData.stats.preguntasHistoria}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-red-700">No vigente</span>
                    <span className="font-semibold text-red-800">0</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-md bg-gradient-to-br from-purple-50 to-purple-100">
            <CardHeader>
              <CardTitle className="text-purple-800">
                Actividad del Mes
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-purple-700">
                    Mundos activos
                  </span>
                  <span className="text-lg font-bold text-purple-800">
                    {dashboardData.stats.mundosActivos}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-purple-700">
                    Etapas creadas
                  </span>
                  <span className="text-lg font-bold text-purple-800">
                    {dashboardData.stats.etapasCreadas}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-purple-700">
                    Preguntas historia
                  </span>
                  <span className="text-lg font-bold text-purple-800">
                    {dashboardData.stats.preguntasHistoria}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-purple-700">
                    Usuarios activos
                  </span>
                  <span className="text-lg font-bold text-purple-800">
                    {dashboardData.stats.usuariosActivos}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </AdminLayout>
  );
};
